/**
 * Shared label/value presentation for labeled layout fields and freeText merge tokens.
 */
import {
  documentSecondaryMetaKind,
  secondaryMetaFieldLabel,
} from '../../leads/utils/offerLeistungszeitraum';
import { isInvoiceFamilyDocumentType } from '../../../utils/documentNumberFormat';
import {
  isLabeledFieldKey,
  type DocumentLayoutV1,
  type LayoutElement,
  type LayoutElementKey,
  type LayoutLabelPlacement,
  type LayoutTextAlign,
  type LabeledFieldKey,
} from './documentLayoutTypes';

/** Only honor an explicit stored align — missing/unknown stays left. */
export function resolveLayoutTextAlign(align?: string | null): LayoutTextAlign {
  if (align === 'right' || align === 'center') return align;
  return 'left';
}

/** Map stored text align to flex justify so HTML/PDF row layouts honor right/center. */
export function layoutAlignToFlexJustify(
  align?: string | null,
): 'flex-start' | 'center' | 'flex-end' {
  const resolved = resolveLayoutTextAlign(align);
  if (resolved === 'right') return 'flex-end';
  if (resolved === 'center') return 'center';
  return 'flex-start';
}

/** Tailwind class to apply explicit align on default-left controls. */
export function layoutAlignTextClass(align?: string | null): string {
  const resolved = resolveLayoutTextAlign(align);
  if (resolved === 'right') return '!text-right';
  if (resolved === 'center') return '!text-center';
  return '';
}

export const DEFAULT_FIELD_LABELS: Record<LabeledFieldKey, string> = {
  documentTitle: 'Rechnung',
  invoiceNumber: 'Rechnungsnr.',
  issueDate: 'Ausstellungsdatum',
  dueDate: 'Fälligkeitsdatum',
  customerNumber: 'Kundennummer',
  projectNumber: 'Projektnummer',
  orderNumber: 'Bestellnummer',
  leistungsort: 'Leistungsort',
  leistungszeitraum: 'Leistungszeitraum',
};

/** Short document-number labels so freeText meta lines stay compact across types. */
const DOCUMENT_NUMBER_FIELD_LABELS: Record<string, string> = {
  Angebot: 'Angebotsnr.',
  Kostenvoranschlag: 'KV-Nr.',
  Auftragsbestätigung: 'AB-Nr.',
  Lieferschein: 'LS-Nr.',
  Rechnung: 'Rechnungsnr.',
  Abschlagsrechnung: 'Rechnungsnr.',
  Schlussrechnung: 'Rechnungsnr.',
  Stornorechnung: 'Rechnungsnr.',
  Gutschrift: 'Gutschriftsnr.',
};

/** Type-aware issue-date labels (standalone freeText + field default). */
const ISSUE_DATE_FIELD_LABELS: Record<string, string> = {
  Angebot: 'Angebotsdatum',
  Kostenvoranschlag: 'KV-Datum',
  Auftragsbestätigung: 'AB-Datum',
  Lieferschein: 'LS-Datum',
  Rechnung: 'Rechnungsdatum',
  Abschlagsrechnung: 'Rechnungsdatum',
  Schlussrechnung: 'Rechnungsdatum',
  Stornorechnung: 'Rechnungsdatum',
  Gutschrift: 'Gutschriftsdatum',
};

/**
 * Runtime label for invoiceNumber / {{invoiceNumberLabel}}.
 * Falls back to Rechnungsnr. for unknown types.
 */
export function documentNumberFieldLabel(documentTypeLabel: string | null | undefined): string {
  const t = (documentTypeLabel ?? '').trim();
  if (!t) return DEFAULT_FIELD_LABELS.invoiceNumber;
  return DOCUMENT_NUMBER_FIELD_LABELS[t] ?? DEFAULT_FIELD_LABELS.invoiceNumber;
}

/**
 * Runtime label for issueDate / {{issueDateLabel}}.
 * Falls back to Ausstellungsdatum for unknown types.
 */
export function issueDateFieldLabel(documentTypeLabel: string | null | undefined): string {
  const t = (documentTypeLabel ?? '').trim();
  if (!t) return DEFAULT_FIELD_LABELS.issueDate;
  return ISSUE_DATE_FIELD_LABELS[t] ?? DEFAULT_FIELD_LABELS.issueDate;
}

/**
 * Runtime label for leistungszeitraum / {{leistungszeitraumLabel}}.
 * Empty when the document type has no secondary meta row.
 */
export function leistungszeitraumFieldLabel(documentTypeLabel: string | null | undefined): string {
  const kind = documentSecondaryMetaKind(documentTypeLabel ?? '');
  if (!kind) return '';
  return secondaryMetaFieldLabel(kind);
}

/** Heading above the contact name in header bands. Shared so HTML preview stays PDF-free. */
export function layoutAnsprechHeading(data: { ansprechHeading?: string }): string {
  return data.ansprechHeading?.trim() || 'Ihr Ansprechpartner';
}

/**
 * Bestellnummer is invoice-family only. Returns the trimmed value for
 * Rechnung / Abschlagsrechnung / Schlussrechnung / Stornorechnung, otherwise null.
 */
export function resolveOrderNumberLabel(
  documentTypeLabel: string | null | undefined,
  raw: string | null | undefined,
): string | null {
  if (!isInvoiceFamilyDocumentType(documentTypeLabel ?? '')) return null;
  const trimmed = (raw ?? '').trim();
  return trimmed || null;
}

/** Empty-title sentinel used when `lead_title` / offerTitleRest is missing. */
export const EMPTY_OFFER_TITLE_SENTINEL = '—';

function isEmptyOfferTitleRest(rest: string): boolean {
  return rest === '' || rest === EMPTY_OFFER_TITLE_SENTINEL;
}

/** ASCII spaces only — NBSP (`\u00a0`) renders as "?" in Helvetica / @react-pdf. */
const OFFER_TITLE_PREFIX_SEP = ' - ';
/** Legacy stored titles used a NBSP after the hyphen. */
const OFFER_TITLE_PREFIX_SEP_NBSP = ' -\u00a0';

/** Non-editable Betreff prefix, e.g. `"Rechnung - "`. Empty when the type is missing. */
export function documentTypeOfferTitlePrefix(documentTypeLabel: string | null | undefined): string {
  const t = (documentTypeLabel ?? '').trim();
  return t ? `${t}${OFFER_TITLE_PREFIX_SEP}` : '';
}

/** Drop a leading `{type} - ` so stored titles are not doubled when the prefix is rendered. */
export function stripDocumentTypeOfferTitlePrefix(
  documentTypeLabel: string | null | undefined,
  rest: string | null | undefined,
): string {
  const raw = (rest ?? '').trim();
  if (isEmptyOfferTitleRest(raw)) return '';
  const type = (documentTypeLabel ?? '').trim();
  if (!type) return raw;
  const lower = raw.toLowerCase();
  const candidates = [
    `${type}${OFFER_TITLE_PREFIX_SEP}`,
    `${type}${OFFER_TITLE_PREFIX_SEP_NBSP}`,
    `${type} - `,
    `${type} -`,
    `${type}-`,
  ];
  for (const candidate of candidates) {
    if (lower.startsWith(candidate.toLowerCase())) {
      const next = raw.slice(candidate.length).trim();
      return isEmptyOfferTitleRest(next) ? '' : next;
    }
  }
  return raw;
}

export type OfferTitleLineOptions = {
  /** User removed the type prefix in the Betreff — render the stored title only. */
  hidePrefix?: boolean;
};

/**
 * Betreff split into the derived type prefix and the stored title, so editor and PDF can
 * lay them out as two boxes that wrap as a unit. Empty title → type only (no dangling dash).
 */
export function offerTitleLineParts(
  documentTypeLabel: string | null | undefined,
  rest: string | null | undefined,
  opts?: OfferTitleLineOptions,
): { prefix: string; title: string } {
  const stripped = stripDocumentTypeOfferTitlePrefix(documentTypeLabel, rest);
  if (opts?.hidePrefix) {
    return { prefix: '', title: stripped || EMPTY_OFFER_TITLE_SENTINEL };
  }
  const prefix = documentTypeOfferTitlePrefix(documentTypeLabel);
  if (stripped) return { prefix, title: stripped };
  if (prefix) return { prefix: '', title: (documentTypeLabel ?? '').trim() };
  return { prefix: '', title: EMPTY_OFFER_TITLE_SENTINEL };
}

/**
 * Full Betreff line for PDF / preview. Empty rest → just the document type (no dangling dash).
 * `{{offerTitle}}` tokens stay rest-only; this is only for the offerTitle field element.
 */
export function formatOfferTitleLine(
  documentTypeLabel: string | null | undefined,
  rest: string | null | undefined,
  opts?: OfferTitleLineOptions,
): string {
  const { prefix, title } = offerTitleLineParts(documentTypeLabel, rest, opts);
  return `${prefix}${title}`;
}

/**
 * Reads back a manually edited Betreff line: the type prefix is derived, so its absence
 * is the signal that the user deleted it.
 */
export function parseEditedOfferTitleLine(
  documentTypeLabel: string | null | undefined,
  edited: string | null | undefined,
): { title: string; hidePrefix: boolean } {
  const raw = (edited ?? '').trim();
  const stripped = stripDocumentTypeOfferTitlePrefix(documentTypeLabel, raw);
  const type = (documentTypeLabel ?? '').trim();
  // Cleared field (or no type at all) falls back to the default prefixed rendering.
  if (!type || isEmptyOfferTitleRest(raw)) return { title: stripped, hidePrefix: false };
  // "Abschlagsrechnung" alone means the title was cleared, not that the prefix was removed.
  if (raw.toLowerCase() === type.toLowerCase()) return { title: '', hidePrefix: false };
  return { title: stripped, hidePrefix: stripped === raw };
}

/** Merge tokens allowed in freeText (whitelist). */
export const LAYOUT_MERGE_TOKENS = [
  'leistungsort',
  'leistungszeitraum',
  'leistungszeitraumLabel',
  'issueDate',
  'issueDateLabel',
  'dueDate',
  'customerNumber',
  'projectNumber',
  'orderNumber',
  'orderNumberLabel',
  'orderTotal',
  'invoiceNumber',
  'invoiceNumberLabel',
  'documentType',
  'documentTitle',
  'offerTitle',
] as const;
export type LayoutMergeToken = (typeof LAYOUT_MERGE_TOKENS)[number];

export type LayoutTokenValues = Partial<Record<LayoutMergeToken, string>>;

export type LabeledFieldPresentation = {
  placement: LayoutLabelPlacement;
  label: string;
  value: string;
  labelBold: boolean;
  labelColor: string;
  labelFontSizePt: number;
  valueBold: boolean;
  valueColor: string;
  valueFontSizePt: number;
  italic: boolean;
  underline: boolean;
  align: 'left' | 'center' | 'right';
};

export function defaultLabelForKey(key: LayoutElementKey): string {
  if (isLabeledFieldKey(key)) return DEFAULT_FIELD_LABELS[key];
  return key;
}

/** Flex/PDF drop a trailing regular space on the label; keep a visible gap after ":". */
export function inlineLabelValueSeparator(key: LayoutElementKey): string {
  return key === 'documentTitle' ? '\u00A0' : ':\u00A0';
}

export function resolveLabelPlacement(el: LayoutElement): LayoutLabelPlacement {
  if (el.labelPlacement === 'inline') return 'inline';
  if (el.labelPlacement === 'stacked') return 'stacked';
  // Belegzeile was historically one line (Dokumenttyp + Nummer).
  if (el.key === 'documentTitle') return 'inline';
  return 'stacked';
}

export function resolveFieldLabel(el: LayoutElement, defaultOverride?: string): string {
  // undefined/null → default label; explicit "" (or whitespace-only) → no label.
  if (el.labelText == null) {
    const override = (defaultOverride ?? '').trim();
    if (override) return override;
    return defaultLabelForKey(el.key);
  }
  return el.labelText.trim();
}

/**
 * Whether the Belegzeile already names the document type, so the Betreff must not repeat it.
 * Without a layout the legacy header always renders `{Typ} {Nummer}`. Layouts that show the
 * number alone (`labelText: ''`) keep the type in the Betreff.
 */
export function documentTitleShowsType(
  layout: DocumentLayoutV1 | null | undefined,
  documentTypeLabel: string | null | undefined,
): boolean {
  if (!layout) return true;
  const defaultLabel = (documentTypeLabel ?? '').trim() || DEFAULT_FIELD_LABELS.documentTitle;
  return [...layout.header.elements, ...layout.footer.elements].some(
    (el) =>
      el.key === 'documentType' ||
      (el.key === 'documentTitle' && resolveFieldLabel(el, defaultLabel) !== ''),
  );
}

export function labeledFieldPresentation(
  el: LayoutElement,
  value: string,
  opts?: { defaultLabel?: string },
): LabeledFieldPresentation {
  const valueFontSizePt = el.fontSizePt ?? 9.5;
  const placement = resolveLabelPlacement(el);
  const label = resolveFieldLabel(el, opts?.defaultLabel);
  const labelFontSizePt =
    el.labelFontSizePt ??
    (placement === 'stacked' ? Math.max(5, valueFontSizePt - 0.5) : valueFontSizePt);
  return {
    placement,
    label,
    value: value.trim() || '—',
    labelBold: el.labelBold === true,
    labelColor: (el.labelColor ?? '').trim() || (placement === 'stacked' ? '#64748b' : el.color || '#374151'),
    labelFontSizePt,
    valueBold: el.bold !== false,
    valueColor: el.color || '#0f172a',
    valueFontSizePt,
    italic: el.italic === true,
    underline: el.underline === true,
    align: resolveLayoutTextAlign(el.align),
  };
}

const TOKEN_RE = /\{\{(\w+)\}\}/g;

/** Hardcoded labels that merge used to bake in; rewrite to dynamic label tokens. */
const HARDCODED_INVOICE_NUMBER_LABELS = [
  'Rechnungsnummer',
  'Rechnungsnr\\.',
  'Rechnungs-Nr\\.',
  'Dokumentnummer',
  'Dokumentnr\\.',
];
const HARDCODED_LEISTUNGSZEITRAUM_LABELS = ['Leistungszeitraum', 'Lieferdatum'];
const HARDCODED_ISSUE_DATE_LABELS = [
  'Rechnungsdatum',
  'Angebotsdatum',
  'Ausstellungsdatum',
  'KV-Datum',
  'AB-Datum',
  'LS-Datum',
];
const HARDCODED_ORDER_NUMBER_LABELS = ['Bestellnummer', 'Bestellnr\\.'];

/**
 * Rewrite known hardcoded labels to label tokens so existing templates stay
 * document-type-aware without re-merging.
 *
 * Handles both:
 * - `Rechnungsnummer: {{invoiceNumber}}` (merged line)
 * - standalone freeText `Rechnungsnummer:` (label + value as separate elements)
 */
export function rewriteHardcodedFreeTextLabels(text: string): string {
  let out = text ?? '';
  if (!out) return '';
  for (const label of HARDCODED_INVOICE_NUMBER_LABELS) {
    out = out.replace(
      new RegExp(`(${label})(\\s*:\\s*)\\{\\{invoiceNumber\\}\\}`, 'gi'),
      '{{invoiceNumberLabel}}$2{{invoiceNumber}}',
    );
  }
  for (const label of HARDCODED_LEISTUNGSZEITRAUM_LABELS) {
    out = out.replace(
      new RegExp(`(${label})(\\s*:\\s*)\\{\\{leistungszeitraum\\}\\}`, 'gi'),
      '{{leistungszeitraumLabel}}$2{{leistungszeitraum}}',
    );
  }
  for (const label of HARDCODED_ISSUE_DATE_LABELS) {
    out = out.replace(
      new RegExp(`(${label})(\\s*:\\s*)\\{\\{issueDate\\}\\}`, 'gi'),
      '{{issueDateLabel}}$2{{issueDate}}',
    );
  }
  for (const label of HARDCODED_ORDER_NUMBER_LABELS) {
    out = out.replace(
      new RegExp(`(${label})(\\s*:\\s*)\\{\\{orderNumber\\}\\}`, 'gi'),
      '{{orderNumberLabel}}$2{{orderNumber}}',
    );
  }
  const trimmed = out.trim();
  for (const label of HARDCODED_INVOICE_NUMBER_LABELS) {
    if (new RegExp(`^${label}\\s*:?\\s*$`, 'i').test(trimmed)) {
      return '{{invoiceNumberLabel}}:';
    }
  }
  for (const label of HARDCODED_LEISTUNGSZEITRAUM_LABELS) {
    if (new RegExp(`^${label}\\s*:?\\s*$`, 'i').test(trimmed)) {
      return '{{leistungszeitraumLabel}}:';
    }
  }
  for (const label of HARDCODED_ISSUE_DATE_LABELS) {
    if (new RegExp(`^${label}\\s*:?\\s*$`, 'i').test(trimmed)) {
      return '{{issueDateLabel}}:';
    }
  }
  for (const label of HARDCODED_ORDER_NUMBER_LABELS) {
    if (new RegExp(`^${label}\\s*:?\\s*$`, 'i').test(trimmed)) {
      return '{{orderNumberLabel}}:';
    }
  }
  return out;
}

export function isLayoutMergeToken(key: string): key is LayoutMergeToken {
  return (LAYOUT_MERGE_TOKENS as readonly string[]).includes(key);
}

function isEmptyTokenValue(raw: string | undefined): boolean {
  const v = (raw ?? '').trim();
  return !v || v === '—';
}

/**
 * Drop "Label: {{token}}" clauses (and neighbouring •/| separators) when the
 * token value is empty — so optional fields like Leistungszeitraum vanish from
 * merged freeText lines instead of rendering as "—".
 *
 * Also drops `{{leistungszeitraumLabel}}: {{leistungszeitraum}}` when the value
 * (or its paired label token) is empty.
 */
export function omitEmptyFreeTextTokenClauses(
  text: string,
  values: LayoutTokenValues,
): string {
  let out = rewriteHardcodedFreeTextLabels(text ?? '');
  if (!out) return '';

  // Pair label-token + value-token before the generic pass so `{{fooLabel}}: {{foo}}`
  // is removed as one clause when the value is empty.
  if (isEmptyTokenValue(values.leistungszeitraum) || isEmptyTokenValue(values.leistungszeitraumLabel)) {
    out = out.replace(
      /(\s*[•·|]\s*)?(\{\{leistungszeitraumLabel\}\}\s*:?\s*)?\{\{leistungszeitraum\}\}/gi,
      '',
    );
    // Standalone label freeText (`{{leistungszeitraumLabel}}:`) or leftover tokens.
    out = out.replace(/(\s*[•·|]\s*)?\{\{leistungszeitraumLabel\}\}\s*:?\s*/gi, '');
  }
  if (isEmptyTokenValue(values.invoiceNumber)) {
    out = out.replace(
      /(\s*[•·|]\s*)?(\{\{invoiceNumberLabel\}\}\s*:?\s*)?\{\{invoiceNumber\}\}/gi,
      '',
    );
    out = out.replace(/(\s*[•·|]\s*)?\{\{invoiceNumberLabel\}\}\s*:?\s*/gi, '');
  }
  if (isEmptyTokenValue(values.issueDate)) {
    out = out.replace(
      /(\s*[•·|]\s*)?(\{\{issueDateLabel\}\}\s*:?\s*)?\{\{issueDate\}\}/gi,
      '',
    );
    out = out.replace(/(\s*[•·|]\s*)?\{\{issueDateLabel\}\}\s*:?\s*/gi, '');
  }
  if (isEmptyTokenValue(values.orderNumber)) {
    if (isEmptyTokenValue(values.orderNumberLabel)) {
      out = out.replace(
        /(\s*[•·|]\s*)?(\{\{orderNumberLabel\}\}\s*:?\s*)?\{\{orderNumber\}\}/gi,
        '',
      );
      out = out.replace(/(\s*[•·|]\s*)?\{\{orderNumberLabel\}\}\s*:?\s*/gi, '');
    } else {
      // Invoice editor: keep the label so an empty Bestellnummer can still be filled.
      out = out.replace(/\{\{orderNumber\}\}/gi, '');
    }
  }

  for (const token of LAYOUT_MERGE_TOKENS) {
    if (
      token === 'invoiceNumberLabel' ||
      token === 'leistungszeitraumLabel' ||
      token === 'issueDateLabel' ||
      token === 'orderNumberLabel'
    ) {
      continue;
    }
    if (token === 'invoiceNumber' || token === 'leistungszeitraum' || token === 'issueDate' || token === 'orderNumber') {
      // Already handled with paired label tokens above; still strip leftover bare tokens.
    }
    if (!isEmptyTokenValue(values[token])) continue;
    out = out.replace(
      new RegExp(`(\\s*[•·|]\\s*)?([^\\n{]*?:\\s*)?\\{\\{${token}\\}\\}`, 'gi'),
      '',
    );
  }
  out = out.replace(/^\s*[•·|]\s*/, '').replace(/\s*[•·|]\s*$/, '');
  out = out.replace(/\s{2,}/g, ' ').trim();
  return out;
}

/** Split freeText into alternating plain / token-value segments for mixed bold rendering. */
export function parseFreeTextWithTokens(
  text: string,
  values: LayoutTokenValues,
): Array<{ text: string; boldValue: boolean }> {
  const prepared = omitEmptyFreeTextTokenClauses(text, values);
  if (!prepared) return [{ text: '', boldValue: false }];
  const out: Array<{ text: string; boldValue: boolean }> = [];
  let last = 0;
  TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN_RE.exec(prepared)) != null) {
    if (m.index > last) {
      out.push({ text: prepared.slice(last, m.index), boldValue: false });
    }
    const key = m[1];
    if (isLayoutMergeToken(key)) {
      const v = (values[key] ?? '').trim() || '—';
      // Label tokens stay regular weight; value tokens are bold.
      const isLabelToken =
        key === 'invoiceNumberLabel' ||
        key === 'leistungszeitraumLabel' ||
        key === 'issueDateLabel' ||
        key === 'orderNumberLabel';
      out.push({ text: v, boldValue: !isLabelToken });
    } else {
      out.push({ text: m[0], boldValue: false });
    }
    last = m.index + m[0].length;
  }
  if (last < prepared.length) {
    out.push({ text: prepared.slice(last), boldValue: false });
  }
  return out.length ? out : [{ text: '', boldValue: false }];
}

/** Plain string for height estimates / accessibility. */
export function expandFreeTextTokens(text: string, values: LayoutTokenValues): string {
  return parseFreeTextWithTokens(text, values)
    .map((s) => s.text)
    .join('');
}

export function tokenChipLabel(token: LayoutMergeToken): string {
  switch (token) {
    case 'leistungsort':
      return 'Leistungsort';
    case 'leistungszeitraum':
      return 'Leistungszeitraum';
    case 'leistungszeitraumLabel':
      return 'Zeitraum-Label';
    case 'issueDate':
      return 'Ausstellungsdatum';
    case 'issueDateLabel':
      return 'Datum-Label';
    case 'dueDate':
      return 'Fälligkeitsdatum';
    case 'customerNumber':
      return 'Kundennummer';
    case 'projectNumber':
      return 'Projektnummer';
    case 'orderNumber':
      return 'Bestellnummer';
    case 'orderNumberLabel':
      return 'Bestellnr.-Label';
    case 'orderTotal':
      return 'Auftragssumme netto';
    case 'invoiceNumber':
      return 'Dokumentnummer';
    case 'invoiceNumberLabel':
      return 'Nummern-Label';
    case 'documentType':
      return 'Dokumenttyp';
    case 'documentTitle':
      return 'Rechnung / Nummer';
    case 'offerTitle':
      return 'Betreff';
    default:
      return token;
  }
}
